/**
 * Converts a section or group name into a URL-friendly slug.
 *
 * "Open Houses"         → "open-houses"
 * "Future Open Houses"  → "future-open-houses"
 * "Listing Attributes"  → "listing-attributes"
 */
export function toSectionSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Finds the first DOM element whose slug ID matches the given hash fragment.
 *
 * Tries in order:
 *   1. Exact getElementById (handles already-slugified hashes)
 *   2. getElementById after slugifying the hash
 *   3. Normalized prefix match against all [data-scroll-target] elements
 *      (strips hyphens so "#openhouse" matches id="open-houses")
 */
export function findScrollTarget(hash: string): HTMLElement | null {
  if (!hash) return null;

  // 1. Exact match
  let target = document.getElementById(hash);
  if (target) return target;

  // 2. Slugified match
  const slug = toSectionSlug(hash);
  target = document.getElementById(slug);
  if (target) return target;

  // 3. Normalized prefix match across scroll-target elements
  const normalized = slug.replace(/-/g, '');
  const candidates = document.querySelectorAll('[data-scroll-target]');
  for (const el of candidates) {
    const candidateNorm = el.id.replace(/-/g, '');
    if (candidateNorm === normalized || candidateNorm.startsWith(normalized)) {
      return el as HTMLElement;
    }
  }

  return null;
}
